import { CheckboxField, Link, NumberField, TextField } from '@flos/react-ui';
import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useState } from 'react';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { useRecoilValue } from 'recoil';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import type {
    CaravanOnsaDTO,
    TrailerOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/uvp';
import { track } from '../../../../utils/trackingUtil';
import { FLAVOUR_NAME } from '../../../../constants/coverable';
import { currentOnsaCoverableSelector } from '../../../../recoil/selectors/currentOnsaCoverableSelector';

const WhichUvpShouldWeInsureStep = ({
    goNext,
    goPrevious,
    renderRegNumberPage
}: {
    goNext: () => void;
    goPrevious: () => void;
    renderRegNumberPage: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const coverable = useRecoilValue<CaravanOnsaDTO | TrailerOnsaDTO>(currentOnsaCoverableSelector);
    const {
        flavourName,
        regNumber: preRegnNmber,
        make: preMake,
        model: preModel,
        year: preModelYear,
        totalWeight: preTotalWeight
    } = coverable;
    const isCaravan = flavourName === FLAVOUR_NAME.UVP_CARAVAN;
    const flavourHippoKey = isCaravan ? 'CARAVAN' : 'TRAILER';
    const shouldPrefillData = Boolean(!preRegnNmber && preMake && preModel && preModelYear);
    const [vehicleInfo, setVehicleInfo] = useState({
        make: shouldPrefillData ? preMake : '',
        model: shouldPrefillData ? preModel : '',
        modelYear: shouldPrefillData ? preModelYear : undefined,
        totalWeight: shouldPrefillData ? preTotalWeight : undefined,
        unknownWeight: shouldPrefillData && !preTotalWeight
    });
    const { make, model, modelYear, totalWeight, unknownWeight } = vehicleInfo;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const nextYear = new Date().getFullYear() + 1;
    const isModelYearValid = (): boolean | undefined => {
        if (modelYear === undefined) return undefined;
        return modelYear >= 1900 && modelYear <= nextYear;
    };

    const getModelYearError = (): string | undefined => {
        if (modelYear === undefined) {
            return undefined;
        }
        if (modelYear < 1900) {
            return `${isCaravan ? 'Caravan' : 'Trailer'} må ikke være fra før år 1900.`;
        }
        if (modelYear > nextYear) {
            return 'Modelår må ikke være mere end et år i fremtiden.';
        }
        return undefined;
    };

    const goToNextPage = (): void => {
        if (make && model && modelYear && (totalWeight || unknownWeight)) {
            setIsLoading(true);
            updateCoverableInformation({
                ...vehicleInfo,
                name: `${make ?? ''} ${model ?? ''}`,
                regNumber: undefined,
                year: modelYear
            });
            const updatedProperties:
                | CoverableInformationToUpdateDTO['caravan']
                | CoverableInformationToUpdateDTO['trailer'] = {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                make,
                model,
                modelYear,
                totalWeight,
                unknownWeight,
                regNumber: undefined
            };
            const updatedCoverable: CoverableInformationToUpdateDTO = {
                [isCaravan ? 'caravan' : 'trailer']: { ...updatedProperties },
                productToUpdate: flavourName
            };
            void saveCoverableInformation(updatedCoverable).then(() => {
                track(
                    `${flavourName}CoverableInformation${isCaravan ? 'Caravan' : 'Trailer'}WithNoLicensePlate`,
                    [{ make }, { model }, { modelYear }, { totalWeight }, { unknownWeight }]
                );
                setIsLoading(false);
                goNext();
            });
        }
    };

    return (
        <WizardPagetemplate
            onNext={goToNextPage}
            onPrevious={goPrevious}
            disableNext={
                isLoading ||
                !make ||
                !model ||
                !isModelYearValid() ||
                (!totalWeight && !unknownWeight)
            }
            hidePrevious={!isCaravan}
            isNextLoading={isLoading}
        >
            <CoverableDetailsTemplate
                header={getHippoText(
                    `DSS-COVERABLE-INFORMATION-STEP-MANUAL-${flavourHippoKey}-INFORMATION-HEADER`
                )}
            >
                <TextField
                    label={getHippoText(
                        `DSS-COVERABLE-INFORMATION-STEP-MANUAL-${flavourHippoKey}-INFORMATION-MAKE`
                    )}
                    value={make}
                    onChange={(event) => {
                        setVehicleInfo((preInfo) => ({
                            ...preInfo,
                            make: event.target.value
                        }));
                    }}
                    disabled={isLoading}
                />
                <>
                    {make && (
                        <TextField
                            label={getHippoText(
                                `DSS-COVERABLE-INFORMATION-STEP-MANUAL-${flavourHippoKey}-INFORMATION-MODEL`
                            )}
                            value={model}
                            onChange={(event) => {
                                setVehicleInfo((preInfo) => ({
                                    ...preInfo,
                                    model: event.target.value
                                }));
                            }}
                            disabled={isLoading}
                        />
                    )}
                </>
                <>
                    {make && model && (
                        <NumberField
                            label={getHippoText(
                                `DSS-COVERABLE-INFORMATION-STEP-MANUAL-${flavourHippoKey}-INFORMATION-MODEL-YEAR`
                            )}
                            value={modelYear?.toString()}
                            pattern="[0-9]+"
                            minLength={4}
                            maxLength={4}
                            isValid={isModelYearValid()}
                            errorText={getModelYearError()}
                            onChange={(event) => {
                                setVehicleInfo((preInfo) => ({
                                    ...preInfo,
                                    modelYear: parseInt(event.target.value)
                                }));
                            }}
                            disabled={isLoading}
                        />
                    )}
                </>
                <>
                    {make && model && modelYear !== undefined && isModelYearValid() && (
                        <>
                            <NumberField
                                label={getHippoText(
                                    `DSS-COVERABLE-INFORMATION-STEP-MANUAL-${flavourHippoKey}-INFORMATION-WEIGHT`
                                )}
                                value={totalWeight?.toString()}
                                pattern="[0-9]+"
                                disabled={isLoading || unknownWeight}
                                onChange={(event) => {
                                    setVehicleInfo((preInfo) => ({
                                        ...preInfo,
                                        totalWeight: parseInt(event.target.value)
                                    }));
                                }}
                            />

                            <CheckboxField
                                label={getHippoText(
                                    `DSS-COVERABLE-INFORMATION-STEP-MANUAL-${flavourHippoKey}-INFORMATION-DO-NOT-KNOW-WEIGHT`
                                )}
                                checked={unknownWeight}
                                onChange={(event) => {
                                    setVehicleInfo((preInfo) => ({
                                        ...preInfo,
                                        totalWeight: event.target.checked ? undefined : totalWeight,
                                        unknownWeight: event.target.checked
                                    }));
                                }}
                                disabled={isLoading}
                            />
                        </>
                    )}
                </>

                <div className="u-text-right">
                    <Link theme={'logo'} onClick={renderRegNumberPage}>
                        {getHippoText(
                            `DSS-COVERABLE-INFORMATION-STEP-MANUAL-${flavourHippoKey}-INFORMATION-USE-LICENSE-PLATE`
                        )}
                    </Link>
                </div>
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};

export default WhichUvpShouldWeInsureStep;
